/**
 * useScreenTracking.ts
 *
 * Reports each screen a staff member opens, for User Audit
 * (plan 2026-10-15-001, U6 / R3). Mounted once from `Layout`, the signed-in
 * staff shell, so public and patient routes never report.
 *
 * Everything decidable without React lives in `lib/screenTracking.ts`: the
 * screen name, the 750 ms settle, the path-and-section dedupe and the request.
 * This hook only feeds it the router location and the session state.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { createScreenTracker, sendScreenView, type ScreenTracker } from '@/lib/screenTracking';

export function useScreenTracking(): void {
  const { pathname, search } = useLocation();
  const trackerRef = useRef<ScreenTracker | null>(null);

  useEffect(() => {
    const tracker = createScreenTracker({
      // Read at send time, not render time: a sign-out that starts during the
      // settle window must stop the report.
      isActive: () => {
        const { token, endReason } = useAuthStore.getState();
        return Boolean(token) && endReason === null;
      },
      send: (view) => {
        const { token, accountId } = useAuthStore.getState();
        if (token) sendScreenView(view, { token, accountId });
      },
    });
    trackerRef.current = tracker;
    return () => {
      tracker.dispose();
      trackerRef.current = null;
    };
  }, []);

  useEffect(() => {
    trackerRef.current?.onLocation(pathname, search);
  }, [pathname, search]);
}
